if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // Preorder confirmation: show banner before adding to cart
        const variantId = this.variantIdInput.value;
        if (
          typeof variantPreorderStatus !== 'undefined' &&
          variantPreorderStatus[variantId] &&
          !this._preorderConfirmed
        ) {
          this.showPreorderConfirmation();
          return;
        }
        this._preorderConfirmed = false;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      showPreorderConfirmation() {
        // Remove existing banner if any
        const existing = this.querySelector('.preorder-confirmation-banner');
        if (existing) existing.remove();

        // Get delivery date from estimated delivery container
        const sectionId = this.dataset.sectionId;
        const deliveryContainer = document.querySelector(
          `#Inventory-${sectionId} .estimated-delivery-date`
        );
        let deliveryDateText = '';
        if (deliveryContainer) {
          const boldEl = deliveryContainer.querySelector('span[style*="font-weight"]');
          if (boldEl) deliveryDateText = boldEl.textContent.trim();
        }

        // Build banner using DOM methods
        const banner = document.createElement('div');
        banner.className = 'preorder-confirmation-banner';

        const message = document.createElement('p');
        message.className = 'preorder-confirmation-banner__message';
        const confirmMessageText = typeof preorderConfirmMessage !== 'undefined' ? preorderConfirmMessage : 'This product is a preorder.';
        const confirmButtonText = typeof preorderConfirmButton !== 'undefined' ? preorderConfirmButton : 'Confirm';
        const cancelButtonText = typeof preorderCancelButton !== 'undefined' ? preorderCancelButton : 'Cancel';

        message.textContent = deliveryDateText
          ? `⚠️ ${confirmMessageText} ${deliveryDateText}.`
          : `⚠️ ${confirmMessageText}`;
        banner.appendChild(message);

        const actions = document.createElement('div');
        actions.className = 'preorder-confirmation-banner__actions';

        const confirmBtn = document.createElement('button');
        confirmBtn.type = 'button';
        confirmBtn.className = 'preorder-confirmation-banner__confirm button button--primary button--small';
        confirmBtn.textContent = confirmButtonText;

        const cancelBtn = document.createElement('button');
        cancelBtn.type = 'button';
        cancelBtn.className = 'preorder-confirmation-banner__cancel button button--secondary button--small';
        cancelBtn.textContent = cancelButtonText;

        actions.appendChild(confirmBtn);
        actions.appendChild(cancelBtn);
        banner.appendChild(actions);

        // Insert after the submit button's parent (.product-form__buttons)
        const buttonsContainer = this.querySelector('.product-form__buttons');
        if (buttonsContainer) {
          buttonsContainer.after(banner);
        } else {
          this.appendChild(banner);
        }

        confirmBtn.addEventListener('click', () => {
          banner.remove();
          this._preorderConfirmed = true;
          this.form.requestSubmit();
        });

        cancelBtn.addEventListener('click', () => {
          banner.remove();
        });

        banner.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
